/**
 * PathProgressPopover — the panel behind the header's remaining-work button.
 *
 * The button says "3 sections, 12 mins left". This answers the obvious follow-up:
 * which sections are done, how far into the current one the user is, and where
 * the remaining minutes sit. A section is two steps — its questionnaire, then
 * the follow-up conversation — so an unresolved row says which of the two still
 * needs the user, and costs whichever of them is left.
 *
 * Read-only by design. Discovery is orchestrator-driven — the state machine
 * decides what comes next — so letting a user jump to an arbitrary section from
 * here would fight it. Rows carry no affordance at all.
 *
 * Composes {@link OptionsDropdown}'s disclosure shape rather than re-deriving its
 * open/close controller (outside-click, Escape, viewport clamp).
 *
 * Opens wherever a mode can describe a path, which includes the Foundation
 * intake prereq: IntakeMode renders discovery's header and supplies a frozen
 * path context, so the button stays live rather than becoming a dead click for
 * the length of the intake conversation. See `_snapshot`.
 *
 * @module components/PathProgressPopover
 */

import { OptionsDropdown } from './OptionsDropdown.js';
import { renderSectionStatusIcon } from './sectionStatusIcon.js';
import { escapeHtml } from '../core/utils.js';
import { Tooltip } from '../utils/Tooltip.js';

/** Panel id, wired to the trigger's aria-controls. */
const PANEL_ID = 'path-progress-panel';

export class PathProgressPopover {
  /**
   * @param {Object} config
   * @param {HTMLElement|null} config.trigger - The `#header-remaining` button. A
   *   null trigger disables the component (the button is markup-optional, and a
   *   stale cached index.html must not brick the header).
   * @param {Object} config.discoveryAPI - DiscoveryAPI instance
   */
  constructor({ trigger, discoveryAPI }) {
    this.trigger = trigger || null;
    this.discoveryAPI = discoveryAPI;

    /**
     * Bumped on every open AND close. A fetch captures it before awaiting and
     * re-checks after; a reply that outlived its panel must not write. Same
     * shape as HeaderContentService._activationToken.
     */
    this._token = 0;

    /** Render state consumed by _render(): 'loading' | 'ready' | 'error'. */
    this._state = 'loading';
    this._sections = [];
    /** Path-level figures snapshotted at open time (see _snapshot). */
    this._percent = 0;
    this._minutesRemaining = 0;
    /** Per-conversation estimate, from the path detail (see _load). */
    this._l2EstimatedMinutes = 5;
    this._pathId = null;
    this._currentQuestionnaireId = null;
    this._currentQuestionnaireName = null;
    /**
     * A row for work that isn't in the path's questionnaire list — the intake
     * conversation. Rendered first and always current. Deliberately NOT merged
     * into _sections: the summary's denominator comes from _sections.length.
     */
    this._syntheticCurrentRow = null;
    /**
     * The mode instance the open snapshot came from. Modes are singletons, so
     * identity is what separates "still the same context" from "switched into
     * or out of intake" — pathId can no longer do that job now that INTAKE and
     * DISCOVERY both report the same one.
     */
    this._mode = null;

    this._observer = null;
    this.dropdown = null;

    if (this.trigger) this._initialize();
  }

  /** @private */
  _initialize() {
    this.dropdown = new OptionsDropdown({
      trigger: this.trigger,
      menuId: PANEL_ID,
      menuClass: 'path-progress-popover',
      body: () => this._render(),
      canOpen: () => this._snapshot(),
      // The trigger carries a hover tooltip; the click that opens the panel
      // must dismiss it so the two don't sit side by side.
      onOpen: () => { Tooltip.removeAll(); this._load(); },
      onClose: () => { this._token++; }
    });

    // Every path that tears the header down hides the button without telling us:
    // dashboard nav (clearModeDisplay → HeaderContentService.hide), a mode
    // switch (deactivateMode), and path completion (rightText goes empty). All
    // three funnel through HeaderManager._writeRemaining toggling `hidden`, so
    // watching that one attribute covers them with no event subscription to leak.
    this._observer = new MutationObserver(() => {
      if (this.trigger.hidden) this.dropdown?.close();
    });
    this._observer.observe(this.trigger, { attributes: true, attributeFilter: ['hidden'] });
  }

  /**
   * Capture the live path context. Doubles as the open guard: no path context,
   * no panel.
   *
   * Asking the mode for a context — rather than testing which mode is mounted —
   * is what lets this stay live across the Foundation intake prereq. IntakeMode
   * renders discovery's header but registers as its own mode, so a name check
   * made the button a silent dead click for the whole intake conversation.
   * Any mode that can describe a path answers this; the rest return null and
   * keep the panel inert. It also avoids a stale read: on a path switch the
   * button's text is rewritten inside a 150ms fade timer, so any id mirrored
   * onto the DOM would lag the mode by that window.
   *
   * @private
   * @returns {boolean} Whether there is a path to describe
   */
  _snapshot() {
    const mode = window.modeOrchestrator?.currentMode;
    // Called as a method so the accessor keeps its receiver — DiscoveryMode's
    // implementation reads live instance fields.
    const context = mode?.getPathContextSnapshot?.();
    if (!context?.pathId) return false;

    // One read, so the summary line and the rows can never come from different
    // moments.
    this._mode = mode;
    this._pathId = context.pathId;
    this._percent = Math.round(Number(context.percent) || 0);
    // Rendered only below 760px, where the trigger collapses to its chevron and
    // the minutes have nowhere else to appear.
    this._minutesRemaining = Number(context.minutesRemaining) || 0;

    // Which section the user is actually in. The API's `status` encodes progress,
    // not position — a section being introduced right now still reports
    // not_started — so without this the panel has no "you are here" and would
    // emphasise whichever row happens to be mid-progress instead. Name is the
    // fallback for stages that set the label but not the id.
    this._currentQuestionnaireId = context.currentQuestionnaireId || null;
    this._currentQuestionnaireName = context.currentQuestionnaireName || null;

    // Work the path's questionnaire list doesn't contain (the intake
    // conversation). Absent in discovery, where every current thing is a real row.
    this._syntheticCurrentRow = context.syntheticCurrentRow || null;

    this._state = 'loading';
    this._sections = [];
    return true;
  }

  /**
   * Fetch the path's sections and repaint. Runs on every open — section statuses
   * change during a session, so a cache would show stale dots.
   * @private
   */
  async _load() {
    const token = ++this._token;
    const pathId = this._pathId;

    let sections;
    let l2EstimatedMinutes;
    try {
      const response = await this.discoveryAPI.getPathById(pathId);
      // _request resolves the raw { success, data } envelope.
      const pathDetail = response?.data ?? response;
      sections = Array.isArray(pathDetail?.questionnaires) ? pathDetail.questionnaires : [];
      // Fallback for a cached bundle talking to an older backend: a missing
      // field must not render "~NaN min" on every unresolved row.
      l2EstimatedMinutes = Number(pathDetail?.l2EstimatedMinutes) || 5;
    } catch (error) {
      console.error('[PathProgressPopover] Failed to load path sections:', error);
      if (token !== this._token) return;
      this._state = 'error';
      this.dropdown?.refreshBody();
      return;
    }

    // Superseded by a close/reopen, or the context changed under us.
    if (token !== this._token) return;

    // Mode identity first, then the path. Comparing pathId alone is no longer
    // sufficient: INTAKE and DISCOVERY both report 'foundation' during the
    // prereq, so a switch between them would slip through a pathId-only check.
    // Modes are singletons, so identity is exact.
    const mode = window.modeOrchestrator?.currentMode;
    if (mode !== this._mode) return;
    if (mode?.getPathContextSnapshot?.()?.pathId !== pathId) return;

    // The header's totals count only active questionnaires; getPathById returns
    // every one the content defines. Without this filter the panel would list
    // sections the summary line above it deliberately excluded.
    this._sections = sections.filter(entry => entry.isActive !== false);
    this._l2EstimatedMinutes = l2EstimatedMinutes;
    this._state = 'ready';
    this.dropdown?.refreshBody();
  }

  /**
   * Panel markup for the current state. Must be synchronous — OptionsDropdown
   * calls it on open and again from refreshBody().
   * @private
   * @returns {string}
   */
  _render() {
    return `${this._renderSummary()}<div class="path-progress-popover__list">${this._renderBody()}</div>`;
  }

  /**
   * Bar, percentage, and a completed count.
   *
   * Deliberately counts DONE rather than remaining: the button that opened this
   * panel already says "N sections, M mins left", and restating it 30px lower
   * would waste the panel's most prominent line.
   *
   * @private
   */
  _renderSummary() {
    const percent = this._percent;
    let line = '';

    if (this._state === 'ready' && this._sections.length > 0) {
      // Backend `status` is the single source of section resolution: complete
      // means the questionnaire is submitted AND its conversation is resolved
      // (or the section was declined). Same rule as the button's
      // `sectionsRemaining`, so the two counts reconcile by construction.
      const done = this._sections.filter(entry => entry.status === 'complete').length;
      line = `${done} of ${this._sections.length} sections complete`;
    }

    // Always emitted; CSS shows it only below 760px. A width check in JS would
    // have to be re-evaluated on resize, and this has no reason to know the
    // viewport at all.
    const minutes = this._minutesRemaining;
    const remaining = minutes > 0
      ? `<div class="path-progress-popover__remaining">${minutes} ${minutes === 1 ? 'minute' : 'minutes'} remaining</div>`
      : '';

    return `
      <div class="path-progress-popover__summary">
        ${remaining}
        <div class="header-linear-progress" role="progressbar" aria-valuenow="${percent}" aria-valuemin="0" aria-valuemax="100">
          <div class="header-linear-progress__fill" style="width:${percent}%"></div>
        </div>
        <span class="path-progress-popover__percent">${percent}%</span>
        ${line ? `<div class="path-progress-popover__summary-line">${line}</div>` : ''}
      </div>
    `;
  }

  /**
   * Whether the user has finished ANSWERING a section's questionnaire —
   * regardless of its conversation. That is the boundary between the section's
   * two steps: it drops the questionnaire's minutes from the row and moves the
   * subtitle on to the follow-up. Never used for status — the check mark
   * requires the section fully resolved (`entry.status === 'complete'`).
   *
   * @private
   * @param {Object} entry
   * @returns {boolean}
   */
  _hasAnsweredQuestionnaire(entry) {
    if (entry.status === 'complete') return true;
    const total = Number(entry.questionsTotal) || 0;
    return entry.status === 'in_progress' && total > 0 && Number(entry.questionsAnswered) === total;
  }

  /**
   * The dot a row should show: the backend's status verbatim, except that the
   * current row is never drawn as untouched. The API's `status` encodes progress
   * and only `questionsAnswered > 0` lifts a section off `not_started`, so the
   * section being introduced right now would otherwise read as not begun — it
   * takes the in-progress ring instead. That is a progress claim, not a fourth
   * "you are here" state: the tint and the row's own `--current` class say
   * position, and `data-status` keeps the raw backend value.
   *
   * `complete` arrives only when the section is resolved (answered +
   * conversation done, or declined), so an answered-but-unconversed section
   * renders as the in-progress ring rather than a premature check.
   *
   * @private
   * @param {Object} entry
   * @param {boolean} isCurrent
   * @returns {'complete'|'in_progress'|'not_started'}
   */
  _iconStatus(entry, isCurrent) {
    const status = entry.status || 'not_started';
    return isCurrent && status === 'not_started' ? 'in_progress' : status;
  }

  /**
   * Which of a section's two steps still needs the user, or null for rows that
   * are not two-step work.
   *
   * Forward-looking by design: an intermediate moment is labelled by the step
   * that hasn't happened yet, so "answered, AI reflecting" and "Go Deeper offer
   * pending" both read as step 2. The live conversational moment is the middle
   * header's job, not this panel's.
   *
   * @private
   * @param {Object} entry
   * @returns {{step: 1|2, label: string}|null}
   */
  _stepInfo(entry) {
    // Prerequisite questionnaires (life-facts, relationship-screening) sit in
    // the same list but back no section, so they are single-step: submitted is
    // finished. An ABSENT flag means an older payload — assume the L2, which is
    // the majority case.
    if (entry.hasL2FollowUp === false) return null;
    // The synthetic intake row carries no questionnaire at all; without this it
    // would fall through to step 1 (undefined === undefined passes the equality
    // below but fails the `> 0` test).
    if (entry.questionsTotal == null) return null;

    if (entry.status === 'complete') return null;
    if (this._hasAnsweredQuestionnaire(entry)) {
      return { step: 2, label: 'Follow-up discussion' };
    }
    return { step: 1, label: 'Questionnaire' };
  }

  /**
   * Whether this row is the section the user is in right now.
   * @private
   * @param {Object} entry
   * @returns {boolean}
   */
  _isCurrent(entry) {
    const q = entry.questionnaire || entry;
    if (this._currentQuestionnaireId) {
      return (entry.questionnaireId || q.id) === this._currentQuestionnaireId;
    }
    return !!this._currentQuestionnaireName && q.name === this._currentQuestionnaireName;
  }

  /** @private */
  _renderBody() {
    if (this._state === 'loading') {
      return Array.from({ length: 4 }, () =>
        '<div class="path-progress-popover__skeleton-row"></div>').join('');
    }
    if (this._state === 'error') {
      return `<div class="path-progress-popover__message">Couldn't load your progress.</div>`;
    }
    if (this._sections.length === 0) {
      return `<div class="path-progress-popover__message">No sections yet.</div>`;
    }

    // Prepended by concatenation, never merged into _sections — _renderSummary
    // takes its denominator from _sections.length, so merging would inflate
    // "2 of 7" to "2 of 8".
    const synthetic = this._syntheticCurrentRow
      ? this._renderRow(this._syntheticCurrentRow, true)
      : '';

    return synthetic + this._sections.map(entry => this._renderRow(entry)).join('');
  }

  /**
   * One section row: dot, name, what it still costs — plus, for work in flight,
   * a second line naming the step. Question counts and a "current task" label
   * stay out: the dot already says where the user is, and the estimate already
   * says what the section costs.
   *
   * The estimate is the row's own share of the button's total, assembled from
   * the same two terms the backend sums: an unanswered questionnaire's duration,
   * plus one conversation for every section not yet resolved. Rows therefore add
   * up to the header figure, and nothing decrements mid-step (the backend counts
   * whole questionnaires).
   *
   * @private
   * @param {Object} entry
   * @param {boolean} [isCurrent] - Override for rows _isCurrent cannot match.
   *   The synthetic intake row has no questionnaire id and, during intake,
   *   both current-questionnaire fields are null — so _isCurrent returns false
   *   for every entry and the "you are here" highlight would be lost silently.
   * @returns {string}
   */
  _renderRow(entry, isCurrent = this._isCurrent(entry)) {
    const q = entry.questionnaire || entry;
    const name = q.name || q.code || q.id || 'Section';
    const status = entry.status || 'not_started';
    const step = this._stepInfo(entry);

    const questionnaireMinutes = this._hasAnsweredQuestionnaire(entry)
      ? 0
      : (Number(q.estimatedDuration) || 0);
    // A step means a conversation is still owed — the same predicate that earns
    // the subtitle, so a row can never be labelled step 2 and cost nothing. The
    // row's own estimate covers every section it serves; the path default only
    // stands in for a row the backend didn't price.
    const l2Minutes = Number(entry.l2EstimatedMinutes) || this._l2EstimatedMinutes;
    const minutes = questionnaireMinutes + (step ? l2Minutes : 0);

    // A not-started section that isn't the current one stays one line: naming
    // its first step there says nothing the grey ring hasn't.
    const showSubtitle = !!step && (isCurrent || status === 'in_progress');
    const subtitle = showSubtitle
      ? `<div class="path-progress-popover__subtitle">${escapeHtml(step.label)} · Step ${step.step} of 2</div>`
      : '';

    return `
      <div class="path-progress-popover__row${isCurrent ? ' path-progress-popover__row--current' : ''}" data-status="${escapeHtml(status)}">
        <span class="path-progress-popover__icon" aria-hidden="true">${renderSectionStatusIcon(this._iconStatus(entry, isCurrent))}</span>
        <div class="path-progress-popover__name-cell">
          <span class="path-progress-popover__name">${escapeHtml(name)}</span>
          ${subtitle}
        </div>
        <span class="path-progress-popover__minutes">${minutes > 0 ? `~${minutes} min` : ''}</span>
      </div>
    `;
  }

  /** Tear down. Safe to call more than once. */
  destroy() {
    this._observer?.disconnect();
    this._observer = null;
    this.dropdown?.destroy();
    this.dropdown = null;
  }
}
